import { FC, useContext } from 'react';

import responseFastDiscardButtonClick, {
    HhtmSource,
    ToState,
} from '@hh.ru/analytics-js-events/build/xhh/employer/responses/resume_card/response_fast_discard_button_click';

import { SendAnalyticsHandler } from 'src/components/ChangeTopicButton';
import ButtonWithSubstates from 'src/components/ChangeTopicButton/ButtonWithSubstates';
import ChangeTopicButtonMagritte from 'src/components/ChangeTopicButton/Magritte';
import { selectorCanCommunicateWithApplicant } from 'src/components/HiringManager/utils/selectorCanCommunicateWithApplicant';
import { ResumeCardActionsContext } from 'src/components/ResumeCard/Actions/Context';
import { useSelector } from 'src/hooks/useSelector';
import { TransitionType } from 'src/models/employerNegotiations/negotiationLinks';
import { VIEW_WITHOUT_CONTACTS } from 'src/utils/constants/resumePermission';

import { getCanMoveTopic } from 'src/components/ResumeCard/Actions/DiscardButton/utils';

const DATA_QA = 'employee-discard-on-topic';

const DiscardButton: FC<{ isMobile?: boolean }> = ({ isMobile }) => {
    const {
        isResponse,
        isDiscard,
        resume,
        isPfpVacancy,
        lastEmployerStateExtName: folderName,
        vacancyId,
        isChangeState,
    } = useContext(ResumeCardActionsContext);
    const hhtmSource = useSelector((state) => state.analyticsParams.hhtmSource) as HhtmSource;
    const { negotiationLinks, hasPfpTopics, topicId } = resume;
    const links = negotiationLinks?.discard?.stateLinks;

    const isArchived = useSelector((state) => state.vacancyResponseFlags.isArchived);
    const canCommunicateApplicant = useSelector(selectorCanCommunicateWithApplicant);
    const isHiringManagerExperiment = useSelector((state) => state.isHiringManagerExperiment);
    const inEmployerSubstatesExp = useSelector((state) => state.isEmployerSubstatesExp);

    const showSubstates = inEmployerSubstatesExp && isResponse && !isArchived;

    const isPfp = isPfpVacancy || !!hasPfpTopics;
    const canMoveTopic = getCanMoveTopic({
        resume,
        isPfp,
        canCommunicateApplicant,
        isHiringManagerExp: isHiringManagerExperiment,
    });

    const handleSendAnalytics: SendAnalyticsHandler = (state) =>
        links &&
        responseFastDiscardButtonClick({
            hhtmSource,
            vacancyId,
            topicId,
            folderName,
            toState: state as ToState,
        });

    if (canMoveTopic && isPfp && links && !resume.lastTopicState?.state.startsWith('DISCARD_')) {
        if (showSubstates) {
            return (
                <ButtonWithSubstates
                    type={TransitionType.Discard}
                    links={links}
                    sendAnalytics={handleSendAnalytics}
                    stretched={!!isMobile}
                />
            );
        }

        return (
            <ChangeTopicButtonMagritte
                links={links}
                size="small"
                mode="secondary"
                showIcon={!isMobile}
                stretched={isMobile}
                sendAnalytics={handleSendAnalytics}
                dataQa={DATA_QA}
            />
        );
    }

    if (
        resume._attributes.permission === VIEW_WITHOUT_CONTACTS ||
        !isDiscard ||
        !links ||
        (!inEmployerSubstatesExp && isChangeState && !isArchived)
    ) {
        return null;
    }

    if (showSubstates) {
        return (
            <ButtonWithSubstates
                type={TransitionType.Discard}
                links={links}
                sendAnalytics={handleSendAnalytics}
                stretched={!!isMobile}
            />
        );
    }

    return (
        <ChangeTopicButtonMagritte
            links={links}
            size="small"
            mode="secondary"
            showIcon={!isMobile}
            stretched={isMobile}
            sendAnalytics={handleSendAnalytics}
            dataQa={DATA_QA}
        />
    );
};

export default DiscardButton;
